import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Chip, Skeleton, Card, CardBody } from '@heroui/react';
import { GraduationCap, AlertCircle, ArrowLeft, CheckCircle2, Clock, BookOpen } from 'lucide-react';
import {
  useCourse,
  useCurrentUserEnrollment,
  useCurrentUserProgress,
  useSelfEnroll,
  useSelfUnenroll,
  useCourseGuidesProgress,
} from '@/hooks/queries/useCourses';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import CourseGuideCard from '@/components/courses/CourseGuideCard';
import PageBackground from '@/components/common/PageBackground';
import { GuideProgressItem } from '@/services/apis/courses';
import { cn } from '@/utils/cn';

const CourseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('courses');
  const { currentLanguage } = useLanguage();
  const { user } = useAuth();

  // TODO: switch to the published-version endpoint when we sort out publishing changes of a course
  const {
    data: course,
    isLoading: courseLoading,
    error: courseError,
  } = useCourse({
    id: id || '',
    shouldSkip: !id,
  });

  const { data: enrollment, isLoading: enrollmentLoading } = useCurrentUserEnrollment(id || '');
  const { data: progress, isLoading: progressLoading } = useCurrentUserProgress(id || '');
  const { data: guidesProgress, isLoading: guidesProgressLoading } = useCourseGuidesProgress(id || '');
  const selfEnrollMutation = useSelfEnroll();
  const selfUnenrollMutation = useSelfUnenroll();
  const isLoading = courseLoading || enrollmentLoading || progressLoading || guidesProgressLoading;
  const isEnrolled = !!enrollment;
  const isCompleted = progress?.isCompleted || false;
  const isSelfEnrolled = isEnrolled && enrollment && user && enrollment.enrolledBy?.id === user.id;

  const displayLanguage = currentLanguage;
  const translation = course?.translations?.find((t) => t.language === displayLanguage) || course?.translations?.[0];
  const courseName = translation?.name || 'Untitled Course';
  const courseDescription = translation?.description || '';

  // Sort guides by position
  const sortedGuides = useMemo(() => {
    if (!course?.courseGuides) return [];
    return [...course.courseGuides].sort((a, b) => a.position - b.position);
  }, [course?.courseGuides]);

  // Build guide progress map
  const guideProgressMap = useMemo(() => {
    if (!guidesProgress?.guideProgress) return new Map<string, GuideProgressItem>();
    const map = new Map<string, GuideProgressItem>();
    for (const progress of guidesProgress.guideProgress) {
      map.set(progress.guideId, progress);
    }
    return map;
  }, [guidesProgress]);

  const handleEnroll = async () => {
    if (!id) return;
    try {
      await selfEnrollMutation.mutateAsync({ courseId: id });
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleUnenroll = async () => {
    if (!id) return;
    try {
      await selfUnenrollMutation.mutateAsync({ courseId: id });
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleBack = () => {
    navigate('/courses');
  };

  // Loading state
  if (isLoading) {
    return (
      <PageBackground>
        <div className="mb-8">
          <Skeleton className="h-12 w-3/4 mb-4 rounded-lg" />
          <Skeleton className="h-6 w-1/2 mb-8 rounded-lg" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      </PageBackground>
    );
  }

  // Error state
  if (courseError || !course) {
    return (
      <PageBackground>
        <div className="mb-8 bg-red-50 dark:bg-red-950/30 border-l-4 border-red-500 rounded-xl p-6 flex items-start gap-4 shadow-lg backdrop-blur-sm">
          <div className="flex-shrink-0 p-2 rounded-lg bg-red-100 dark:bg-red-900/50">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-red-900 dark:text-red-200 mb-1">{t('detail.error.title')}</h3>
            <p className="text-sm text-red-700 dark:text-red-300 mb-4">
              {courseError?.message || t('detail.error.message')}
            </p>
            <Button color="primary" variant="flat" onPress={handleBack}>
              {t('detail.error.backToCourses')}
            </Button>
          </div>
        </div>
      </PageBackground>
    );
  }

  const guideCount = sortedGuides.length;
  const estimatedTime = sortedGuides.reduce((total, cg) => {
    const guideTime = cg.guide?.estimatedTime || 0;
    return total + guideTime;
  }, 0);
  const estimatedTimeText = estimatedTime > 0 ? `${estimatedTime} min` : null;

  const authorName = course.author
    ? `${course.author.firstName || ''} ${course.author.lastName || ''}`.trim() || course.author.email
    : 'Unknown';

  return (
    <PageBackground>
      {/* Back Button */}
      <div className="mb-6">
        <Button
          variant="light"
          onPress={handleBack}
          startContent={<ArrowLeft className="w-4 h-4" />}
          className="text-gray-600 dark:text-gray-400"
        >
          {t('detail.backToCourses')}
        </Button>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur-lg opacity-50" />
            <div className="relative p-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
              <GraduationCap className="w-10 h-10 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent mb-3">
                  {courseName}
                </h1>
                {courseDescription && (
                  <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-4">{courseDescription}</p>
                )}
              </div>
            </div>

            {/* Badges and Stats */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              {course.isRecommended && (
                <Chip size="sm" color="primary" variant="flat">
                  {t('detail.recommended')}
                </Chip>
              )}
              {isEnrolled && (
                <Chip
                  size="sm"
                  color={isCompleted ? 'success' : 'primary'}
                  variant="flat"
                  startContent={isCompleted ? <CheckCircle2 className="w-3 h-3" /> : undefined}
                >
                  {isCompleted ? t('detail.completed') : t('detail.enrolled')}
                </Chip>
              )}
              {estimatedTimeText && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">{estimatedTimeText}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <BookOpen className="w-4 h-4" />
                <span className="font-medium">
                  {guideCount} {guideCount === 1 ? t('detail.guide') : t('detail.guides')}
                </span>
              </div>
            </div>

            {/* Author */}
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
              <span className="font-medium">
                {t('detail.by')} {authorName}
              </span>
            </div>

            {/* Enroll/Unenroll Buttons */}
            {!isEnrolled && (
              <Button
                color="primary"
                variant="solid"
                size="lg"
                onPress={handleEnroll}
                isLoading={selfEnrollMutation.isPending}
                className={cn(
                  'font-semibold shadow-lg hover:shadow-xl transition-all duration-300',
                  'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600',
                  'hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700',
                  'text-white border-0',
                )}
                startContent={<GraduationCap className="w-5 h-5" />}
              >
                {t('detail.enroll')}
              </Button>
            )}
            {isSelfEnrolled && (
              <Button
                color="danger"
                variant="bordered"
                size="lg"
                onPress={handleUnenroll}
                isLoading={selfUnenrollMutation.isPending}
                className="font-semibold"
              >
                {t('detail.unenroll')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Guides Section */}
      {sortedGuides.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('detail.guidesTitle')}</h2>
          <div className="space-y-4">
            {sortedGuides.map((courseGuide, index) => {
              const previousGuideId = index > 0 && sortedGuides[index - 1] ? sortedGuides[index - 1].guide.id : null;
              return (
                <CourseGuideCard
                  key={courseGuide.id}
                  courseGuide={courseGuide}
                  index={index}
                  guideProgressMap={guideProgressMap}
                  isEnrolled={isEnrolled}
                  previousGuideId={previousGuideId}
                  language={displayLanguage}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Empty Guides State */}
      {sortedGuides.length === 0 && (
        <Card className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl border border-gray-200/50 dark:border-gray-800/50">
          <CardBody className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('detail.noGuides.title')}</h3>
            <p className="text-gray-600 dark:text-gray-400">{t('detail.noGuides.message')}</p>
          </CardBody>
        </Card>
      )}
    </PageBackground>
  );
};

export default CourseDetailPage;
